import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { getMyAppointments } from '../services/doctorsApi';
import { cancelAppointment } from '../services/consultationApi';
import { getRatingForAppointment } from '../services/ratingsApi';
import RatingModal from '../components/RatingModal';

const STATUS_META = {
  CONFIRMED: { label: 'Confirmed', color: '#059669', bg: '#ecfdf5', border: '#6ee7b7' },
  PENDING:   { label: 'Pending',   color: '#d97706', bg: '#fffbeb', border: '#fde68a' },
  CANCELLED: { label: 'Cancelled', color: '#dc2626', bg: '#fef2f2', border: '#fca5a5' },
  COMPLETED: { label: 'Completed', color: '#6366f1', bg: '#eef2ff', border: '#c7d2fe' },
  NO_SHOW:   { label: 'No Show',   color: '#92400e', bg: '#fef3c7', border: '#fde68a' },
};

const RELATION_LABELS = {
  SELF: 'Self', SPOUSE: 'Spouse', CHILD: 'Child',
  PARENT: 'Parent', SIBLING: 'Sibling', OTHER: 'Other',
};

const TABS = ['upcoming', 'past', 'all'];

const StatusBadge = ({ status }) => {
  const m = STATUS_META[status] || STATUS_META['PENDING'];
  return (
    <span style={{
      fontSize: 11, fontWeight: 600, padding: '3px 9px', borderRadius: 9999,
      background: m.bg, color: m.color, border: `1px solid ${m.border}`,
      fontFamily: 'DM Sans, sans-serif', whiteSpace: 'nowrap',
    }}>
      {m.label}
    </span>
  );
};

const ModeChip = ({ mode }) => (
  <span style={{
    fontSize: 11, fontWeight: 600, padding: '3px 9px', borderRadius: 9999,
    background: mode === 'online' ? '#ecfeff' : '#eef2ff',
    color: mode === 'online' ? '#0891b2' : '#4f46e5',
    border: `1px solid ${mode === 'online' ? '#a5f3fc' : '#c7d2fe'}`,
    fontFamily: 'DM Sans, sans-serif', whiteSpace: 'nowrap',
  }}>
    {mode === 'online' ? '📱 Online' : '🏥 In-person'}
  </span>
);

const SkeletonRow = () => (
  <div style={{ display: 'flex', gap: 14, padding: '16px 0', borderBottom: '1px solid #f3f4f6', alignItems: 'center' }}>
    <div className="skeleton" style={{ width: 48, height: 48, borderRadius: '50%', flexShrink: 0 }} />
    <div style={{ flex: 1 }}>
      <div className="skeleton skeleton-line" style={{ width: '40%', height: 13, marginBottom: 6 }} />
      <div className="skeleton skeleton-line" style={{ width: '60%', height: 11 }} />
    </div>
    <div className="skeleton" style={{ width: 80, height: 22, borderRadius: 9999 }} />
  </div>
);

/* ── Confirm Cancel Dialog ──────────────────────────────────────── */
const CancelDialog = ({ appt, onConfirm, onClose, loading }) => (
  <div
    style={{
      position: 'fixed', inset: 0, zIndex: 9999,
      background: 'rgba(15,10,40,0.5)', backdropFilter: 'blur(4px)',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      padding: '20px 16px', animation: 'fadeUp .2s ease both',
    }}
    onClick={e => { if (e.target === e.currentTarget) onClose(); }}
  >
    <div style={{
      background: '#fff', borderRadius: 18, width: '100%', maxWidth: 400,
      padding: '28px 24px',
      boxShadow: '0 8px 40px rgba(220,38,38,0.15), 0 2px 8px rgba(0,0,0,0.08)',
      animation: 'dropIn .22s ease both',
    }}>
      <div style={{ fontSize: 36, textAlign: 'center', marginBottom: 12 }}>🚫</div>
      <div style={{ fontFamily: 'Sora, sans-serif', fontWeight: 700, fontSize: 17, color: '#1e1b4b', textAlign: 'center', marginBottom: 8 }}>
        Cancel Appointment?
      </div>
      <div style={{ fontSize: 13.5, color: '#6b7280', fontFamily: 'DM Sans, sans-serif', textAlign: 'center', marginBottom: 24, lineHeight: 1.6 }}>
        You're about to cancel your appointment with{' '}
        <strong style={{ color: '#1e1b4b' }}>{appt?.doctorName || 'your doctor'}</strong>.
        This cannot be undone.
      </div>
      <div style={{ display: 'flex', gap: 10 }}>
        <button
          onClick={onClose}
          disabled={loading}
          style={{
            flex: 1, padding: '11px 0', borderRadius: 12, border: '1.5px solid #e5e7eb',
            background: '#fff', color: '#374151', fontSize: 14, fontWeight: 600,
            cursor: 'pointer', fontFamily: 'DM Sans, sans-serif',
          }}
        >
          Keep it
        </button>
        <button
          onClick={onConfirm}
          disabled={loading}
          style={{
            flex: 1, padding: '11px 0', borderRadius: 12, border: 'none',
            background: loading ? '#fca5a5' : '#dc2626', color: '#fff',
            fontSize: 14, fontWeight: 700, cursor: loading ? 'not-allowed' : 'pointer',
            fontFamily: 'DM Sans, sans-serif', transition: 'opacity .12s',
          }}
        >
          {loading ? 'Cancelling…' : 'Yes, cancel'}
        </button>
      </div>
    </div>
  </div>
);

/* ── Appointment Card ────────────────────────────────────────────── */
const AppointmentCard = ({ appt, onCancel, onViewResults, onRate, ratedIds }) => {
  const dateStr = appt.appointmentDate
    ? new Date(appt.appointmentDate).toLocaleDateString(undefined, { weekday: 'short', month: 'short', day: 'numeric' })
    : '—';

  const showCancel      = appt.status === 'CONFIRMED';
  const showViewResults = appt.consultationAvailable === true || appt.status === 'COMPLETED';
  const showRate        = appt.status === 'COMPLETED' && !ratedIds.has(appt.id);
  const alreadyRated    = appt.status === 'COMPLETED' && ratedIds.has(appt.id);

  return (
    <div style={{
      background: '#fff', border: '1px solid #f3f4f6', borderRadius: 16,
      padding: '18px 20px', display: 'flex', gap: 16, alignItems: 'flex-start',
      transition: 'box-shadow .15s',
    }}
      onMouseEnter={e => e.currentTarget.style.boxShadow = '0 4px 20px rgba(79,70,229,0.08)'}
      onMouseLeave={e => e.currentTarget.style.boxShadow = 'none'}
    >
      {/* Avatar */}
      <div style={{
        width: 48, height: 48, borderRadius: '50%', flexShrink: 0,
        background: 'linear-gradient(135deg, #eef2ff, #ede9fe)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        fontSize: 20,
      }}>
        {appt.doctorPhotoUrl
          ? <img src={appt.doctorPhotoUrl} alt="" style={{ width: 48, height: 48, borderRadius: '50%', objectFit: 'cover' }} />
          : '👨‍⚕️'}
      </div>

      {/* Info */}
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontFamily: 'Sora, sans-serif', fontWeight: 700, fontSize: 14.5, color: '#1e1b4b', marginBottom: 3 }}>
          {appt.doctorName || 'Doctor'}
        </div>
        <div className="t-body-sm" style={{ marginBottom: 4 }}>
          {appt.doctorDepartment || appt.department || 'General Medicine'}
        </div>
        {(appt.patientName || appt.name) && (
          <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 6 }}>
            <span style={{
              fontSize: 11, fontWeight: 600, padding: '2px 8px', borderRadius: 9999,
              background: '#fffbeb', color: '#d97706', border: '1px solid #fde68a',
              fontFamily: 'DM Sans, sans-serif',
            }}>
              👤 {appt.patientName || appt.name}
            </span>
            {appt.relation && appt.relation !== 'SELF' && (
              <span style={{
                fontSize: 11, fontWeight: 500, padding: '2px 8px', borderRadius: 9999,
                background: '#f5f3ff', color: '#6d28d9', border: '1px solid #ddd6fe',
                fontFamily: 'DM Sans, sans-serif',
              }}>
                {RELATION_LABELS[appt.relation] || appt.relation}
              </span>
            )}
          </div>
        )}
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
          <span className="t-meta">📅 {dateStr}</span>
          {appt.slotTime && <span className="t-meta">🕐 {appt.slotTime}</span>}
          <ModeChip mode={appt.mode || 'online'} />
          {appt.isSecondOpinion && (
            <span style={{
              fontSize: 11, fontWeight: 600, padding: '3px 9px', borderRadius: 9999,
              background: '#fdf4ff', color: '#9333ea', border: '1px solid #e9d5ff',
              fontFamily: 'DM Sans, sans-serif',
            }}>🔍 2nd Opinion</span>
          )}
        </div>

        {/* Action buttons */}
        {(showCancel || showViewResults || showRate || alreadyRated) && (
          <div style={{ display: 'flex', gap: 8, marginTop: 12, flexWrap: 'wrap', alignItems: 'center' }}>
            {showViewResults && (
              <button
                onClick={() => onViewResults(appt)}
                style={{
                  fontSize: 12, fontWeight: 700, padding: '6px 14px', borderRadius: 9999,
                  background: 'linear-gradient(135deg, #eef2ff, #ede9fe)',
                  color: '#4f46e5', border: '1px solid #c7d2fe',
                  cursor: 'pointer', fontFamily: 'DM Sans, sans-serif',
                  transition: 'all .12s', display: 'flex', alignItems: 'center', gap: 5,
                }}
                onMouseEnter={e => { e.currentTarget.style.background = '#4f46e5'; e.currentTarget.style.color = '#fff'; }}
                onMouseLeave={e => { e.currentTarget.style.background = 'linear-gradient(135deg, #eef2ff, #ede9fe)'; e.currentTarget.style.color = '#4f46e5'; }}
              >
                🔍 View Results
              </button>
            )}
            {showRate && (
              <button
                onClick={() => onRate(appt)}
                style={{
                  fontSize: 12, fontWeight: 700, padding: '6px 14px', borderRadius: 9999,
                  background: 'linear-gradient(135deg, #fffbeb, #fef3c7)',
                  color: '#d97706', border: '1px solid #fde68a',
                  cursor: 'pointer', fontFamily: 'DM Sans, sans-serif',
                  transition: 'all .12s', display: 'flex', alignItems: 'center', gap: 5,
                }}
                onMouseEnter={e => { e.currentTarget.style.background = '#f59e0b'; e.currentTarget.style.color = '#fff'; e.currentTarget.style.borderColor = '#f59e0b'; }}
                onMouseLeave={e => { e.currentTarget.style.background = 'linear-gradient(135deg, #fffbeb, #fef3c7)'; e.currentTarget.style.color = '#d97706'; e.currentTarget.style.borderColor = '#fde68a'; }}
              >
                ⭐ Rate Doctor
              </button>
            )}
            {alreadyRated && (
              <span style={{
                fontSize: 11, fontWeight: 600, padding: '4px 10px', borderRadius: 9999,
                background: '#f0fdf4', color: '#16a34a', border: '1px solid #bbf7d0',
                fontFamily: 'DM Sans, sans-serif',
              }}>
                ✓ Rated
              </span>
            )}
            {showCancel && (
              <button
                onClick={() => onCancel(appt)}
                style={{
                  fontSize: 12, fontWeight: 600, padding: '6px 14px', borderRadius: 9999,
                  background: '#fef2f2', color: '#dc2626', border: '1px solid #fca5a5',
                  cursor: 'pointer', fontFamily: 'DM Sans, sans-serif', transition: 'all .12s',
                }}
                onMouseEnter={e => { e.currentTarget.style.background = '#dc2626'; e.currentTarget.style.color = '#fff'; }}
                onMouseLeave={e => { e.currentTarget.style.background = '#fef2f2'; e.currentTarget.style.color = '#dc2626'; }}
              >
                Cancel
              </button>
            )}
          </div>
        )}
      </div>

      {/* Status */}
      <div style={{ textAlign: 'right', flexShrink: 0, display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 6 }}>
        <StatusBadge status={appt.status || 'CONFIRMED'} />
        {appt.confirmationId && (
          <span className="t-meta" style={{ fontSize: 11 }}>#{appt.confirmationId}</span>
        )}
      </div>
    </div>
  );
};

/* ── Main Component ──────────────────────────────────────────────── */
const Appointments = () => {
  const navigate = useNavigate();

  const [appointments, setAppointments]   = useState([]);
  const [loading, setLoading]             = useState(true);
  const [error, setError]                 = useState(null);
  const [tab, setTab]                     = useState('upcoming');
  const [search, setSearch]               = useState('');

  // Cancel dialog state
  const [cancelTarget, setCancelTarget]   = useState(null);
  const [cancelling, setCancelling]       = useState(false);
  const [cancelError, setCancelError]     = useState(null);

  // Rating state
  const [rateTarget, setRateTarget]       = useState(null);
  const [ratedIds, setRatedIds]           = useState(new Set());

  // View results handler — navigate to the results page
  const handleViewResults = (appt) => {
    navigate(`/appointments/${appt.id}/results`);
  };

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await getMyAppointments();
      setAppointments(data);

      // Check which completed appointments have already been rated
      const completedIds = data
        .filter(a => a.status === 'COMPLETED')
        .map(a => a.id);

      const checks = await Promise.allSettled(
        completedIds.map(id => getRatingForAppointment(id))
      );

      const alreadyRated = new Set();
      checks.forEach((result, idx) => {
        if (result.status === 'fulfilled' && result.value !== null) {
          alreadyRated.add(completedIds[idx]);
        }
      });
      setRatedIds(alreadyRated);
    } catch (e) {
      setError(`Could not load appointments (${e.message})`);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  /* ── Cancel handlers ── */
  const handleCancelClick   = (appt) => { setCancelTarget(appt); setCancelError(null); };
  const handleCancelClose   = () => { if (!cancelling) setCancelTarget(null); };
  const handleCancelConfirm = async () => {
    if (!cancelTarget) return;
    setCancelling(true);
    setCancelError(null);
    try {
      await cancelAppointment(cancelTarget.id);
      setAppointments(prev =>
        prev.map(a => a.id === cancelTarget.id ? { ...a, status: 'CANCELLED' } : a)
      );
      setCancelTarget(null);
    } catch (e) {
      setCancelError(e?.response?.data?.message || 'Could not cancel. Please try again.');
    } finally {
      setCancelling(false);
    }
  };

  /* ── Rating handlers ── */
  const handleRateClick = (appt) => setRateTarget(appt);
  const handleRateClose = () => setRateTarget(null);
  const handleRateSubmitted = (result) => {
    setRatedIds(prev => new Set([...prev, result.appointmentId]));
  };

  const now = new Date();
  const filtered = appointments.filter(a => {
    const apptDate = a.appointmentDate ? new Date(a.appointmentDate) : null;
    const isUpcoming = apptDate ? apptDate >= now : true;
    const isPast     = apptDate ? apptDate < now  : false;

    if (tab === 'upcoming' && !isUpcoming) return false;
    if (tab === 'past'     && !isPast)     return false;

    if (search.trim()) {
      const q = search.toLowerCase();
      return (
        (a.doctorName || '').toLowerCase().includes(q) ||
        (a.department || '').toLowerCase().includes(q) ||
        (a.confirmationId || '').toLowerCase().includes(q)
      );
    }
    return true;
  });

  const counts = {
    upcoming: appointments.filter(a => a.appointmentDate ? new Date(a.appointmentDate) >= now : true).length,
    past:     appointments.filter(a => a.appointmentDate ? new Date(a.appointmentDate) < now  : false).length,
    all:      appointments.length,
  };

  return (
    <div className="page">
      <main className="page-main">

        <div className="breadcrumb">
          <button className="t-link" onClick={() => navigate('/')}>Home</button>
          <span>›</span>
          <span className="bc-current">Appointments</span>
        </div>

        <div className="fade-up" style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', flexWrap: 'wrap', gap: 12, marginBottom: '1.5rem' }}>
          <div>
            <h1 className="t-h1" style={{ marginBottom: 4 }}>📅 My Appointments</h1>
            <p className="t-body-sm">Track and manage your scheduled consultations</p>
          </div>
          <button
            className="btn-primary btn-auto"
            onClick={() => navigate('/services/online-consultation')}
            style={{ display: 'flex', alignItems: 'center', gap: 6 }}
          >
            <span style={{ fontSize: 18 }}>+</span>
            Book new
          </button>
        </div>

        {/* Stat strip */}
        <div className="stat-grid fade-up-1" style={{ marginBottom: '1.5rem' }}>
          {[
            { icon: '📅', label: 'Total',    value: counts.all,      color: '#4f46e5' },
            { icon: '⏳', label: 'Upcoming', value: counts.upcoming, color: '#d97706' },
            { icon: '✅', label: 'Past',     value: counts.past,     color: '#059669' },
          ].map(s => (
            <div key={s.label} className="stat-card" style={{ borderLeft: `3px solid ${s.color}` }}>
              <div style={{ fontSize: 22, marginBottom: 8 }}>{s.icon}</div>
              <div className="stat-value" style={{ color: s.color }}>{loading ? '—' : s.value}</div>
              <div className="stat-label">{s.label}</div>
            </div>
          ))}
        </div>

        {/* Tabs + search */}
        <div className="card fade-up-2" style={{ marginBottom: '1.25rem', padding: '14px 18px' }}>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10, alignItems: 'center' }}>
            <div className="toggle-group">
              {TABS.map(t => (
                <button
                  key={t}
                  className={`toggle-item${tab === t ? ' active' : ''}`}
                  onClick={() => setTab(t)}
                >
                  {t.charAt(0).toUpperCase() + t.slice(1)}
                  {counts[t] > 0 && (
                    <span style={{ marginLeft: 5, fontSize: 11, fontWeight: 700 }}>
                      ({counts[t]})
                    </span>
                  )}
                </button>
              ))}
            </div>

            <div className="search-wrap" style={{ flex: '1 1 200px', minWidth: 180, marginBottom: 0 }}>
              <svg className="search-icon" width="16" height="16" fill="none" stroke="#9ca3af" strokeWidth="2" viewBox="0 0 24 24">
                <circle cx="11" cy="11" r="8"/><path d="m21 21-4.35-4.35" strokeLinecap="round"/>
              </svg>
              <input
                value={search}
                onChange={e => setSearch(e.target.value)}
                className="search-input"
                placeholder="Search by doctor, department…"
                style={{ height: 36, fontSize: 13 }}
              />
              {search && (
                <button className="search-clear" onClick={() => setSearch('')} style={{ fontSize: 16 }}>×</button>
              )}
            </div>
          </div>
        </div>

        {error && (
          <div className="error-state" style={{ marginBottom: '1rem' }}>
            <span>⚠️</span> {error}
            <button className="t-link" style={{ marginLeft: 12 }} onClick={load}>Retry</button>
          </div>
        )}

        {/* Cancel error inline */}
        {cancelError && !cancelTarget && (
          <div className="error-state" style={{ marginBottom: '1rem' }}>
            <span>⚠️</span> {cancelError}
            <button className="t-link" style={{ marginLeft: 10 }} onClick={() => setCancelError(null)}>Dismiss</button>
          </div>
        )}

        {/* List */}
        <div className="fade-up-3" style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          {loading
            ? Array.from({ length: 4 }).map((_, i) => <SkeletonRow key={i} />)
            : filtered.length === 0
              ? (
                <div className="empty-state">
                  <div className="empty-icon">📅</div>
                  <p className="empty-title">No appointments found</p>
                  <p className="empty-body">
                    {tab === 'upcoming'
                      ? 'You have no upcoming appointments.'
                      : tab === 'past'
                      ? 'No past appointments on record.'
                      : 'You have no appointments yet.'}
                  </p>
                  <button
                    className="btn-primary"
                    style={{ maxWidth: 200, margin: '0 auto' }}
                    onClick={() => navigate('/services/online-consultation')}
                  >
                    Book a consultation
                  </button>
                </div>
              )
              : filtered.map((appt, i) => (
                <div key={appt.id || i} style={{ animation: `fadeUp .3s ${i * 30}ms ease both` }}>
                  <AppointmentCard
                    appt={appt}
                    onCancel={handleCancelClick}
                    onViewResults={handleViewResults}
                    onRate={handleRateClick}
                    ratedIds={ratedIds}
                  />
                </div>
              ))
          }
        </div>

      </main>

      {/* Cancel confirmation dialog */}
      {cancelTarget && (
        <CancelDialog
          appt={cancelTarget}
          loading={cancelling}
          onConfirm={handleCancelConfirm}
          onClose={handleCancelClose}
        />
      )}
      {cancelError && cancelTarget && (
        <div style={{
          position: 'fixed', bottom: 24, left: '50%', transform: 'translateX(-50%)',
          background: '#fef2f2', border: '1px solid #fca5a5', borderRadius: 10,
          padding: '10px 20px', color: '#dc2626', fontSize: 13,
          fontFamily: 'DM Sans, sans-serif', fontWeight: 600, zIndex: 10000,
          boxShadow: '0 4px 16px rgba(220,38,38,0.15)',
        }}>
          ⚠️ {cancelError}
        </div>
      )}

      {/* Rating modal */}
      {rateTarget && (
        <RatingModal
          appointment={rateTarget}
          onClose={handleRateClose}
          onSubmitted={handleRateSubmitted}
        />
      )}

    </div>
  );
};

export default Appointments;